// eslint-disable-next-line no-unused-vars
const analyticsTypes = require('../jsdoc-types');
const {
  HashmapStorageFactory,
  HASHMAP_STORAGE_TYPE,
} = require('../../common/utils/hashmapStorage');
const {
  isVariationGroupId,
  isVariationProductId,
} = require('../../common/helpers/productIdHelpers');
const { isDoubleLook } = require('../../common/utils/doubleLookUtils');

let fallbackItemList;

/**
 * @typedef {Object} ItemListAndPosition
 * @property {string} list - The list content as a string.
 * @property {number} position - The current index in the list.
 */

/**
 * Creates an object containing a string list and a selected index.
 *
 * @param {string} list - A string representing the list of a product.
 * @param {number} position - The one-based position of the product in that list.
 * @returns {ItemListAndPosition} Object with list and position for analytics tracking.
 */
function createItemListAndPosition(list, position) {
  return { list, position };
}

/**
 * Check whether the param is an instance of ItemListAndPosition
 * @param {any} obj target
 * @returns {void} true if the target is an ItemListAndPosition
 * @throws {TypeError}
 */
function assertItemListAndPosition(obj) {
  const isValid = 'list' in obj && 'position' in obj;
  if (!isValid) {
    throw new TypeError('Illegal argument: item is not a ItemListAndPosition');
  }
}

const impressionItemListStorage = HashmapStorageFactory.createHashmapStorage({
  type: HASHMAP_STORAGE_TYPE.SESSION_STORAGE,
  name: 'ga.itemList.impressions',
  validateItem: assertItemListAndPosition,
  validateKey: (key) => {
    if (!isVariationGroupId(key) && !isDoubleLook(key)) {
      throw new TypeError(
        `Illegal argument: "${key}" is not a variation group ID `
      );
    }
  },
});

const clickedItemListStorage = HashmapStorageFactory.createHashmapStorage({
  type: HASHMAP_STORAGE_TYPE.SESSION_STORAGE,
  name: 'ga.itemList.clicked',
  validateItem: assertItemListAndPosition,
  validateKey: (key) => {
    if (!isVariationGroupId(key) && !isDoubleLook(key)) {
      throw new TypeError(
        `Illegal argument: "${key}" is not a variation group ID `
      );
    }
  },
});

const cartItemListStorage = HashmapStorageFactory.createHashmapStorage({
  type: HASHMAP_STORAGE_TYPE.LOCAL_STORAGE,
  name: 'ga.itemList.cart',
  validateItem: assertItemListAndPosition,
  validateKey: (key) => {
    if (!isVariationProductId(key)) {
      throw new TypeError(`Illegal argument: "${key}" is not a variant ID `);
    }
  },
});

/**
 * Returns the list considered as fallback ('direct', 'other', 'home'...).
 * @returns {string|null} The name of the list
 */
function getFallbackItemList() {
  return fallbackItemList;
}

/**
 * Saves the list considered as fallback ('direct', 'other', 'home'...).
 * @param {string} listName the name of the list to set as fallback
 * @returns {void}
 */
function setFallbackItemList(listName) {
  fallbackItemList = listName;
}

module.exports = {
  cartItemListStorage,
  clickedItemListStorage,
  createItemListAndPosition,
  getFallbackItemList,
  impressionItemListStorage,
  setFallbackItemList,
};
